import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input, Alert } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/FilterBar';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { RelativeTime } from '../../components/RelativeTime';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { cancelIntentsFixture } from './fixture';

/**
 * Cancel-Intents · § 312q-Anträge.
 * Spec-Quelle: 01_apis/01_core.api.valitype.net/ADMIN/views.md → #/cancel-intents
 *
 * Read-only Liste — Confirm-Action passiert via Mail-Link beim Customer,
 * nicht über Operator-UI. Operator sieht nur Status-History.
 */
export function CancelIntentsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  const query = useQuery({
    queryKey: ['cancelIntents', { search, status }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return cancelIntentsFixture({ search, status });
      }
      const params = new URLSearchParams();
      if (search) params.set('email', search);
      if (status) params.set('status', status);
      return fetch(`/v1/admin/cancel-intents?${params.toString()}`);
    },
  });

  const rows = query.data?.intents ?? [];

  return (
    <div>
      <PageHeader
        title="Cancel-Intents (§ 312q)"
        subtitle="Read-only · Confirmation passiert via Mail-Link beim Kunden, nicht über Operator-UI."
      />

      <Alert color="info" className="small">
        Diese Liste zeigt alle Kündigungs-Intents nach <strong>§ 312q BGB</strong>.
        Operator kann hier nur einsehen — der "Confirm"-Klick geschieht durch den
        Kunden über den Bestätigungs-Link in der E-Mail.
      </Alert>

      <FilterBar
        search={search}
        searchPlaceholder="Email-Substring…"
        onSearchChange={setSearch}
        onReset={
          search || status
            ? () => {
                setSearch('');
                setStatus('');
              }
            : undefined
        }
        extras={
          <Input
            type="select"
            bsSize="sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ maxWidth: 160 }}
          >
            <option value="">Alle Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </Input>
        }
      />

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!rows.length}
        emptyMessage="Keine Cancel-Intents."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 90 }}>ID</th>
            <th>Email</th>
            <th style={{ width: 120 }}>Subscription</th>
            <th style={{ width: 110 }}>Status</th>
            <th>Grund</th>
            <th style={{ width: 140 }}>Erstellt</th>
            <th style={{ width: 140 }}>Bestätigt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className={c.status === 'cancelled' ? 'opacity-50' : undefined}>
              <td>#{c.id}</td>
              <td>{c.customerEmail}</td>
              <td>
                <code style={{ fontSize: 13 }}>#{c.subscriptionId}</code>
              </td>
              <td>
                <StatusBadge value={c.status} />
              </td>
              <td className="small text-muted">
                {c.reason ?? <em>kein Grund angegeben</em>}
              </td>
              <td>
                <RelativeTime iso={c.createdAt} />
              </td>
              <td>
                {c.confirmedAt ? (
                  <RelativeTime iso={c.confirmedAt} />
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </div>
  );
}
